import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import {
  Button, InputField, ErrorMessage, FieldGroup, TooltipWidget,
} from 'anf-core-react';
import { TmntText } from '../Common';
import { FormCell, FormGroup, FormWrapper } from '../FormStructure';
import isValidOrderId from '../../tools/isValidOrderId';
import emailValidator from '../../tools/emailValidator';
import { ORDERHELP_FORM_QUERY } from './operations';
import { ERROR_MESSAGE } from '../Messages/Messages';
import { textProp } from '../../tools/customProps';

export default function OrderTrackingForm({
  orderNumberText, emailAddressText, orderNumberTooltipText, orderHelpButtonText, toolTipLabel,
}) {
  const [orderNumberInput, setOrderNumberInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [orderNumberValidErrMsg, setOrderNumberValidErrMsg] = useState('');
  const [emailInputInvalidErrMsg, setEmailInputInvalidErrMsg] = useState('');
  const [isTooltipOpen, setIsToolTipOpen] = useState(false);
  const [urlParams, setUrlParams] = useState({});
  const { data: queryData, loading, error: queryError } = useQuery(ORDERHELP_FORM_QUERY);

  useEffect(() => {
    if (queryData) {
      const { config } = queryData;
      const { orderDetailDisplayUrl } = config || {};
      const extractParamsFromUrl = (url) => {
        try {
          const urlObj = new URL(url, window.location.origin);
          const params = new URLSearchParams(urlObj.search);
          return {
            storeId: params.get('storeId'),
            catalogId: params.get('catalogId'),
            langId: params.get('langId'),
          };
        } catch {
          return {};
        }
      };
      const params = extractParamsFromUrl(orderDetailDisplayUrl);
      setUrlParams(params);
    }
  }, [queryData]);

  const { textFor, config } = queryData || {};
  const {
    orderNumberErrorValidation,
    emailAddressErrorValidation,
    fromOrderNumberText,
    formError,
  } = textFor || {};

  const { orderDetailDisplayUrl } = config || {};
  const { userType } = queryData || {};

  if (loading) return <FormCell isLoading />;
  if (queryError) return ERROR_MESSAGE;

  const validateOrderNumber = () => {
    const isValidOrderNumber = isValidOrderId(orderNumberInput);
    if (!isValidOrderNumber) {
      setOrderNumberValidErrMsg(orderNumberErrorValidation?.value);
    }
    return isValidOrderNumber;
  };

  const validateEmail = () => {
    const isValidEmailId = emailValidator(emailInput);
    if (!isValidEmailId) {
      setEmailInputInvalidErrMsg(emailAddressErrorValidation?.value);
    }
    return isValidEmailId;
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    setOrderNumberValidErrMsg('');
    setEmailInputInvalidErrMsg('');

    if (validateOrderNumber() && validateEmail()) {
      const form = document.getElementById('order-tracking__form');

      // Build action URL based on base URL's existing params to avoid duplicates
      const destUrl = new URL(orderDetailDisplayUrl, window.location.origin);
      const params = new URLSearchParams(destUrl.search);

      // Merge hidden form fields first to keep consistent keys (storeId, catalogId, langId, userType)
      const formData = new FormData(form);
      formData.forEach((value, key) => {
        // Only set if not already present to avoid duplicates from base URL
        if (!params.has(key)) params.set(key, value);
      });

      // Override with user-entered values
      params.set('orderNumber', orderNumberInput.trim());
      params.set('orderEmail', emailInput.trim());

      // Persist params so the Order Details page can recover them after locale changes
      try {
        const payload = {
          orderNumber: orderNumberInput.trim(),
          orderEmail: emailInput.trim(),
          storeId: urlParams.storeId,
          catalogId: urlParams.catalogId,
          langId: urlParams.langId,
          userType,
        };
        window.sessionStorage.setItem('orderDetailsParams', JSON.stringify(payload));
      } catch {
        // no-op
      }

      destUrl.search = `?${params.toString()}`;
      form.action = destUrl.toString();
      form.submit();
    }
  };

  const renderToolTipWidget = () => {
    const handleTooltipOnClick = () => setIsToolTipOpen(!isTooltipOpen);
    const handleOnKeyUp = (event) => {
      event.stopPropagation();
      setIsToolTipOpen(event.key !== 'Escape');
    };

    return (
      <TooltipWidget
        buttonLabelText={toolTipLabel}
        contentPosition="top-center"
        contentID="help-order-form-tooltip"
        isOpen={isTooltipOpen}
        onClick={handleTooltipOnClick}
        onKeyUp={handleOnKeyUp}
      >
        <TmntText tmnt={orderNumberTooltipText} />
      </TooltipWidget>
    );
  };

  return (
    <form id="order-tracking__form" action={orderDetailDisplayUrl} method="post" data-testid="order-tracking-form">
      <FormWrapper>
        <FormGroup>
          <h2>{fromOrderNumberText?.value}</h2>
          <input type="hidden" name="storeId" value={urlParams.storeId || ''} />
          <input type="hidden" name="catalogId" value={urlParams.catalogId || ''} />
          <input type="hidden" name="langId" value={urlParams.langId || ''} />
          <input type="hidden" name="userType" value={userType || ''} />
          <FormCell>
            <FieldGroup legend="Help Order Form">
              <InputField
                autoComplete="off"
                id="trackorder-ordernumber-input"
                name="orderNumber"
                label={orderNumberText}
                isInvalid={orderNumberValidErrMsg !== ''}
                onChange={(e) => setOrderNumberInput(e.target.value)}
                isRequired
                value={orderNumberInput}
              >
                {renderToolTipWidget()}
                {orderNumberValidErrMsg && (
                <ErrorMessage id="orderInput_errorMessage" variant="error">
                  {orderNumberValidErrMsg}
                </ErrorMessage>
                )}
              </InputField>
              <InputField
                autoComplete="email"
                id="trackorder-email-input"
                name="orderEmail"
                label={emailAddressText}
                isInvalid={emailInputInvalidErrMsg !== ''}
                onChange={(e) => setEmailInput(e.target.value)}
                type="email"
                isRequired
                value={emailInput}
              >
                {emailInputInvalidErrMsg && (
                <ErrorMessage id="orderemail_errorMessage" variant="error">
                  {emailInputInvalidErrMsg}
                </ErrorMessage>
                )}
              </InputField>
            </FieldGroup>
          </FormCell>
          {(orderNumberValidErrMsg || emailInputInvalidErrMsg) && (
          <ErrorMessage id="orderemail_errorMessage" variant="error">
            {formError?.value}
          </ErrorMessage>
          )}
          <FormCell />
          <Button
            isFullWidth
            variant="tertiary-light"
            className="self-service-button"
            onClick={handleOnSubmit}
          >
            <TmntText
              testId="order-tracking-submit-button"
              tmnt={{ key: 'orderHelpButtonText', value: orderHelpButtonText }}
            />
          </Button>
        </FormGroup>
      </FormWrapper>
    </form>
  );
}

OrderTrackingForm.propTypes = {
  orderNumberText: PropTypes.string.isRequired,
  emailAddressText: PropTypes.string.isRequired,
  orderNumberTooltipText: textProp.isRequired,
  orderHelpButtonText: PropTypes.string.isRequired,
  toolTipLabel: PropTypes.string.isRequired,
};

OrderTrackingForm.defaultProps = {};
